import { useEffect, useState, useCallback } from 'react';
import { promptsAPI } from '../api/client';
import {
  FileText,
  Plus,
  Check,
  ChevronLeft,
  Clock,
  Save,
  Flag,
} from 'lucide-react';

interface PromptVersion {
  id: number;
  version: number;
  content: string;
  is_active: boolean;
  waffle_flag: string;
  notes: string;
  created_by: number | null;
  created_by_name: string | null;
  created_at: string;
}

interface PromptTemplateSummary {
  id: number;
  slug: string;
  name: string;
  description: string;
  active_version: { id: number; version: number } | null;
  version_count: number;
  updated_at: string;
}

interface PromptTemplateDetail {
  id: number;
  slug: string;
  name: string;
  description: string;
  versions: PromptVersion[];
  created_at: string;
  updated_at: string;
}

export default function PromptsAdmin() {
  const [templates, setTemplates] = useState<PromptTemplateSummary[]>([]);
  const [selected, setSelected] = useState<PromptTemplateDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showNewVersion, setShowNewVersion] = useState(false);
  const [newContent, setNewContent] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [newFlag, setNewFlag] = useState('');
  const [activateOnCreate, setActivateOnCreate] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const fetchTemplates = useCallback(async () => {
    try {
      const data = await promptsAPI.list();
      setTemplates(data);
    } catch {
      setMessage({ type: 'error', text: 'Failed to load prompt templates' });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  useEffect(() => {
    if (message) {
      const t = setTimeout(() => setMessage(null), 4000);
      return () => clearTimeout(t);
    }
  }, [message]);

  const openTemplate = async (slug: string) => {
    try {
      setIsLoading(true);
      const data = await promptsAPI.get(slug);
      setSelected(data);
      setShowNewVersion(false);
    } catch {
      setMessage({ type: 'error', text: 'Failed to load template' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateVersion = async () => {
    if (!selected || !newContent.trim()) return;
    setIsSaving(true);
    try {
      await promptsAPI.createVersion(selected.slug, {
        content: newContent,
        notes: newNotes,
        waffle_flag: newFlag,
        activate: activateOnCreate,
      });
      setMessage({ type: 'success', text: 'New version created' });
      setShowNewVersion(false);
      setNewContent('');
      setNewNotes('');
      setNewFlag('');
      setActivateOnCreate(false);
      await openTemplate(selected.slug);
      await fetchTemplates();
    } catch {
      setMessage({ type: 'error', text: 'Failed to create version' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleActivate = async (versionId: number) => {
    if (!selected) return;
    try {
      await promptsAPI.activateVersion(selected.slug, versionId);
      setMessage({ type: 'success', text: 'Version activated' });
      await openTemplate(selected.slug);
      await fetchTemplates();
    } catch {
      setMessage({ type: 'error', text: 'Failed to activate version' });
    }
  };

  const startNewVersion = () => {
    if (selected) {
      const active = selected.versions.find(v => v.is_active && !v.waffle_flag);
      setNewContent(active?.content ?? '');
      setNewNotes('');
      setNewFlag('');
      setActivateOnCreate(false);
      setShowNewVersion(true);
    }
  };

  if (isLoading && !selected && templates.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      {/* Flash message */}
      {message && (
        <div className={`mb-4 p-3 rounded-lg text-sm ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {message.text}
        </div>
      )}

      {!selected ? (
        /* -------- Template list -------- */
        <>
          <h1 className="text-2xl font-bold text-gray-900 mb-6">Prompt Management</h1>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {templates.map(t => (
              <button
                key={t.slug}
                onClick={() => openTemplate(t.slug)}
                className="text-left bg-white rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start gap-3">
                  <FileText size={20} className="text-navy mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">{t.name}</h3>
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">{t.description}</p>
                    <div className="flex items-center gap-3 mt-3 text-xs text-gray-400">
                      <span>v{t.active_version?.version ?? '?'} active</span>
                      <span>{t.version_count} version{t.version_count !== 1 ? 's' : ''}</span>
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </>
      ) : (
        /* -------- Template detail -------- */
        <>
          <div className="flex items-center gap-3 mb-6">
            <button onClick={() => { setSelected(null); setShowNewVersion(false); }} className="text-gray-500 hover:text-gray-800">
              <ChevronLeft size={20} />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold text-gray-900 truncate">{selected.name}</h1>
              <p className="text-sm text-gray-500">{selected.description}</p>
            </div>
            <button
              onClick={startNewVersion}
              className="flex items-center gap-2 px-4 py-2 bg-gold-dark text-white rounded-lg hover:bg-gold-dark text-sm shrink-0"
            >
              <Plus size={16} /> New Version
            </button>
          </div>

          {/* New version form */}
          {showNewVersion && (
            <div className="bg-white rounded-xl shadow-sm border border-gold/30 p-5 mb-6">
              <h3 className="font-semibold text-gray-900 mb-3">Create New Version</h3>
              <textarea
                value={newContent}
                onChange={e => setNewContent(e.target.value)}
                rows={14}
                className="w-full border border-gray-300 rounded-lg p-3 text-sm font-mono focus:ring-2 focus:ring-gold focus:border-gold"
                placeholder="Prompt content..."
              />
              <div className="grid sm:grid-cols-2 gap-4 mt-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Notes / changelog</label>
                  <input
                    value={newNotes}
                    onChange={e => setNewNotes(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gold focus:border-gold"
                    placeholder="What changed?"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Waffle flag (A/B testing, optional)</label>
                  <input
                    value={newFlag}
                    onChange={e => setNewFlag(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gold focus:border-gold"
                    placeholder="e.g. new_system_prompt_v2"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between mt-4">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={activateOnCreate}
                    onChange={e => setActivateOnCreate(e.target.checked)}
                    className="rounded border-gray-300 text-gold focus:ring-gold"
                  />
                  Activate immediately
                </label>
                <div className="flex gap-2">
                  <button onClick={() => setShowNewVersion(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateVersion}
                    disabled={isSaving || !newContent.trim()}
                    className="flex items-center gap-2 px-4 py-2 bg-gold-dark text-white rounded-lg hover:bg-gold-dark text-sm disabled:opacity-50"
                  >
                    <Save size={16} /> {isSaving ? 'Saving...' : 'Save Version'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Version list */}
          <div className="space-y-4">
            {selected.versions.map(v => (
              <div
                key={v.id}
                className={`bg-white rounded-xl shadow-sm border p-5 ${v.is_active && !v.waffle_flag ? 'border-green-300 ring-1 ring-green-200' : 'border-gray-200'}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-gray-900">v{v.version}</span>
                    {v.is_active && !v.waffle_flag && (
                      <span className="flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                        <Check size={12} /> Active
                      </span>
                    )}
                    {v.waffle_flag && (
                      <span className="flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full">
                        <Flag size={12} /> {v.waffle_flag}
                        {v.is_active && ' (active)'}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    {!v.is_active && (
                      <button
                        onClick={() => handleActivate(v.id)}
                        className="text-xs text-navy hover:text-navy font-medium"
                      >
                        Activate
                      </button>
                    )}
                  </div>
                </div>

                <details className="group">
                  <summary className="text-xs text-gray-500 cursor-pointer select-none hover:text-gray-700">
                    Show prompt content ({v.content.length.toLocaleString()} chars)
                  </summary>
                  <pre className="mt-2 p-3 bg-gray-50 rounded-lg text-xs font-mono text-gray-700 whitespace-pre-wrap max-h-80 overflow-y-auto">
                    {v.content}
                  </pre>
                </details>

                <div className="flex items-center gap-4 mt-3 text-xs text-gray-400">
                  <span className="flex items-center gap-1"><Clock size={12} /> {new Date(v.created_at).toLocaleDateString()}</span>
                  {v.created_by_name && <span>by {v.created_by_name}</span>}
                  {v.notes && <span className="text-gray-500 truncate max-w-xs">{v.notes}</span>}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
